// services/approvalAPI.ts
import axiosClient from './axiosClient';

// Types based on your API schema
export interface PendingApproval {
  asset_id: number;
  category_id: number;
  request_note: string;
  priority: 'low' | 'medium' | 'high';
  id: number;
  user_id: number;
  company_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  asset_name: string;
  category_name: string;
  company_name: string;
  approver_name: string;
}

export interface ApprovalActionData {
  status: string;
  notes: string;
  rejection_reason: string;
  request_id: number;
}

export interface ApprovalResponse {
  status: string;
  notes: string;
  rejection_reason: string;
  id: number;
  request_id: number;
  approved_by: number;
  approved_at: string;
}

export interface ApprovalHistory {
  status: string;
  notes: string;
  rejection_reason: string;
  id: number;
  request_id: number;
  approved_by: number;
  approved_at: string;
}

// Approval API functions
export const approvalAPI = {
  /**
   * Get pending approvals for a company
   */
  getPendingApprovals: (company_id: number): Promise<PendingApproval[]> => {
    return axiosClient
      .get('/approvals/pending', {
        params: { company_id }
      })
      .then(response => response.data);
  },

  /**
   * Approve a specific request
   */
  approveRequest: (
    request_id: number,
    data: Omit<ApprovalActionData, 'request_id'>
  ): Promise<ApprovalResponse> => {
    const payload: ApprovalActionData = {
      ...data,
      request_id // Ensure request_id is included in the payload
    };

    return axiosClient
      .post(`/approvals/${request_id}/approve`, payload)
      .then(response => response.data);
  },

  /**
   * Reject a specific request
   */
  rejectRequest: (
    request_id: number,
    data: Omit<ApprovalActionData, 'request_id'>
  ): Promise<ApprovalResponse> => {
    const payload: ApprovalActionData = {
      ...data,
      request_id // Ensure request_id is included in the payload
    };

    return axiosClient
      .post(`/approvals/${request_id}/reject`, payload)
      .then(response => response.data);
  },

  /**
   * Get approval history with pagination
   */
  getApprovalHistory: (
    company_id: number,
    skip: number = 0,
    limit: number = 100
  ): Promise<ApprovalHistory[]> => {
    return axiosClient
      .get('/approvals/history', {
        params: { company_id, skip, limit }
      })
      .then(response => response.data);
  },

  /**
   * Get approved requests waiting for asset assignment
   */
  getApprovedRequests: (
    company_id: number,
    skip: number = 0,
    limit: number = 100
  ): Promise<PendingApproval[]> => {
    return axiosClient
      .get('/approvals/approved', {
        params: { company_id, skip, limit }
      })
      .then(response => response.data);
  },

  /**
   * Assign an existing asset to an approved request
   */
  assignAssetToRequest: (
    request_id: number,
    asset_id: number
  ): Promise<string> => {
    return axiosClient
      .put(`/requests/${request_id}/assign-asset?asset_id=${asset_id}`)
      .then(response => response.data);
  }
};

export default approvalAPI;
